import React from 'react';
import {useTestsState} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {getCategoryStats} from '@/modules/patient-dashboard/utils/healthCategories';
import {useDashboardData} from '@/modules/patient-dashboard/hooks/useDashboardData';
import Icon from '@/shared/components/Icon';

interface HorizontalCategoryChartProps {
    selectedCategory?: string;
    onCategorySelect?: (categoryKey: string) => void;
}

// Map category keys to their corresponding colors
const CATEGORY_COLORS: Record<string, string> = {
    heart: '#ef829c',
    micronutrient: '#ebc191',
    'liver-kidney': '#8fdbb6',
    hormone: '#a0e4dd',
    metabolic: '#c7b8de',
    other: '#94a3b8', // Default gray for other categories
};

// Lighter gradient start colors per category, matching the design's
// soft-to-saturated bar style.
const CATEGORY_GRADIENT_START: Record<string, string> = {
    heart: '#fce4ea',
    micronutrient: '#f9e8d0',
    'liver-kidney': '#dcf2e5',
    hormone: '#e2f5f3',
    metabolic: '#ece5f5',
    other: '#e2e8f0',
};

// Fixed display order matching the design's "By Category" list. Any
// categories not in this list fall to the end. Stable order avoids the
// row-reshuffling effect that sort-by-score produced when results changed.
const CATEGORY_ORDER = ['heart', 'micronutrient', 'liver-kidney', 'hormone', 'metabolic'];

const HorizontalCategoryChart: React.FC<HorizontalCategoryChartProps> = ({selectedCategory, onCategorySelect}) => {
    const testsState = useTestsState();
    const isLoading = testsState.loading;

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: currentPatientData} = useDashboardData();

    // Get all results for the current patient
    const allResults = React.useMemo(() => {
        return currentPatientData.flatMap(
            (test: any) =>
                test.results?.map((result: any) => ({
                    ...result,
                    createdDate: test.createdDate,
                })) || []
        );
    }, [currentPatientData]);

    // Get category stats
    const categoryStats = getCategoryStats(allResults);

    const categoryData = React.useMemo(() => {
        return categoryStats
            .filter((category) => category.key !== 'other')
            .map((category) => ({
                key: category.key,
                name: category.name,
                value: Math.round(category.averageValue),
                isSelected: selectedCategory === category.key,
                hasTests: category.availableTests > 0,
            }))
            .sort((a, b) => {
                const ai = CATEGORY_ORDER.indexOf(a.key);
                const bi = CATEGORY_ORDER.indexOf(b.key);
                return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
            });
    }, [categoryStats, selectedCategory]);

    // Handle category click
    const handleCategoryClick = (categoryKey: string, hasTests: boolean) => {
        if (onCategorySelect && hasTests) {
            onCategorySelect(categoryKey);
        }
    };

    if (isLoading) {
        return (
            <div className="space-y-3">
                {Array.from({length: 5}).map((_, index) => (
                    <div key={index} className="animate-pulse">
                        <div className="flex items-center justify-between mb-1">
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                            <div className="h-4 bg-gray-200 rounded w-8"></div>
                        </div>
                        <div className="h-6 bg-gray-200 rounded"></div>
                    </div>
                ))}
            </div>
        );
    }

    if (categoryData.length === 0) {
        return (
            <div className="flex items-center justify-center h-32 text-gray-500">
                <p className="text-sm">No category data available</p>
            </div>
        );
    }

    return (
        <div>
            {categoryData.map((category) => {
                    const barWidth = category.hasTests ? Math.max(category.value, 5) : 0;
                    const gradientStart =
                        CATEGORY_GRADIENT_START[category.key] || CATEGORY_GRADIENT_START.other;
                    const gradientEnd = CATEGORY_COLORS[category.key] || CATEGORY_COLORS.other;
                    const isActive = category.isSelected && category.hasTests;
                    return (
                        <div
                            key={category.key}
                            className={`transition-all duration-200 border-b border-gray-100 last:border-b-0 relative ${
                                category.hasTests
                                    ? 'cursor-pointer hover:bg-gray-50'
                                    : 'cursor-not-allowed opacity-50'
                            }`}
                            onClick={() => handleCategoryClick(category.key, category.hasTests)}>
                            <div className="grid grid-cols-[16px_minmax(150px,auto)_minmax(0,1fr)_36px] items-center gap-3 pl-2 pr-4 py-2">
                                {/* Selected chevron — cyan (mld-lab-trend) per source */}
                                <div className="flex justify-center">
                                    {isActive && (
                                        <Icon name="chevron-right" className="h-4 w-4 text-mld-lab-trend" />
                                    )}
                                </div>
                                {/* Category name — selected row is cyan (mld-lab-trend) */}
                                <div className="flex items-center min-w-0">
                                    <span
                                        className={`text-base whitespace-nowrap ${
                                            isActive
                                                ? 'font-medium text-mld-lab-trend'
                                                : 'font-normal text-mld-text-primary'
                                        }`}>
                                        {category.name}
                                    </span>
                                </div>
                                {/* Gradient bar — light → saturated left-to-right per design.
                                    h-6 (24px) matches the source bar measured on the 1120px-scaled crop (~25px). */}
                                <div className="h-6 rounded-full bg-gray-100 overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all duration-300 ease-out"
                                        style={{
                                            width: `${barWidth}%`,
                                            background: `linear-gradient(90deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
                                        }}
                                    />
                                </div>
                                {/* Score */}
                                <span className="text-lg font-normal text-mld-text-primary text-right">
                                    {!category.hasTests || category.value === 0 ? 'N/A' : category.value}
                                </span>
                            </div>
                        </div>
                    );
            })}
        </div>
    );
};

export default HorizontalCategoryChart;
